/**
 * Secure Storage Layer
 *
 * Provides secure storage abstraction with:
 * - Data sensitivity classification
 * - Automatic encryption for sensitive data
 * - sessionStorage for temporary data
 * - localStorage cleanup on logout
 * - XSS protection through CSP
 */

import CryptoJS from 'crypto-js';
import { logger } from '@/utils/logger';

// ============================================================================
// CONSTANTS
// ============================================================================

// Data sensitivity levels
export enum SensitivityLevel {
  PUBLIC = 'public',           // Theme, language - no protection needed
  SESSION = 'session',         // Search queries - clear on tab close
  PRIVATE = 'private',         // User preferences - encrypt
  SENSITIVE = 'sensitive',     // Tokens - handled by Supabase, encrypt
}

// Storage keys registry
export const STORAGE_KEYS = {
  // PUBLIC (localStorage, no encryption)
  THEME: 'theme',
  LANGUAGE: 'app-locale',
  COOKIE_CONSENT: 'cookie_consent',

  // SESSION (sessionStorage, no encryption)
  SESSION_ID: 'session_id',
  SEARCH_HISTORY: 'search_history',
  WIZARD_PROGRESS: 'wizard_progress',
  TEMP_UPLOADS: 'temp_uploads',

  // PRIVATE (localStorage, encrypted)
  ORG_ID: 'subsidypilot_current_organization_id',
  USER_PREFERENCES: 'user_preferences',
  AI_TRIAL_DATA: 'ai_trial_data',

  // SENSITIVE (Supabase manages, or encrypted)
  // Auth tokens handled by Supabase automatically
} as const;

// ============================================================================
// ENCRYPTION
// ============================================================================

/**
 * Get encryption key that works across tabs
 * Key is stored in localStorage to be consistent across all tabs
 * Combined with user-agent for additional entropy
 */
function getEncryptionKey(): string {
  // Use localStorage instead of sessionStorage for multi-tab support
  const storageKey = localStorage.getItem('__storage_key') || generateStorageKey();

  // Combine with user-agent for additional entropy
  const entropy = `${storageKey}-${navigator.userAgent.substring(0, 20)}`;

  return CryptoJS.SHA256(entropy).toString();
}

/**
 * Generate storage encryption key (persists across tabs and sessions)
 * New key is generated on first visit or after logout clears it
 */
function generateStorageKey(): string {
  const key = CryptoJS.lib.WordArray.random(32).toString();
  localStorage.setItem('__storage_key', key);
  return key;
}

/**
 * Encrypt data using AES
 */
function encrypt(data: string): string {
  const key = getEncryptionKey();
  return CryptoJS.AES.encrypt(data, key).toString();
}

/**
 * Decrypt data using AES
 */
function decrypt(encryptedData: string): string {
  const key = getEncryptionKey();
  const bytes = CryptoJS.AES.decrypt(encryptedData, key);
  return bytes.toString(CryptoJS.enc.Utf8);
}

// ============================================================================
// SECURE STORAGE INTERFACE
// ============================================================================

export interface SecureStorageOptions {
  sensitivity: SensitivityLevel;
  ttl?: number; // Time to live in milliseconds
}

class SecureStorage {
  /**
   * Set item in appropriate storage with encryption if needed
   */
  setItem(key: string, value: any, options: SecureStorageOptions): void {
    const stringValue = JSON.stringify(value);
    const { sensitivity, ttl } = options;

    // Add expiration if TTL specified
    const data = ttl
      ? JSON.stringify({ value: stringValue, expires: Date.now() + ttl })
      : stringValue;

    switch (sensitivity) {
      case SensitivityLevel.PUBLIC:
        // Store plaintext in localStorage
        localStorage.setItem(key, data);
        break;

      case SensitivityLevel.SESSION:
        // Store plaintext in sessionStorage (cleared on tab close)
        sessionStorage.setItem(key, data);
        break;

      case SensitivityLevel.PRIVATE:
      case SensitivityLevel.SENSITIVE:
        // Encrypt and store in localStorage
        const encrypted = encrypt(data);
        localStorage.setItem(key, encrypted);
        break;
    }
  }

  /**
   * Get item from storage with automatic decryption
   */
  getItem(key: string, options: SecureStorageOptions): any {
    const { sensitivity } = options;
    let data: string | null = null;

    switch (sensitivity) {
      case SensitivityLevel.PUBLIC:
        data = localStorage.getItem(key);
        break;

      case SensitivityLevel.SESSION:
        data = sessionStorage.getItem(key);
        break;

      case SensitivityLevel.PRIVATE:
      case SensitivityLevel.SENSITIVE:
        const encrypted = localStorage.getItem(key);
        if (encrypted) {
          try {
            data = decrypt(encrypted);
          } catch (error) {
            console.warn('[SecureStorage] Decryption failed for', key);
            return null;
          }
        }
        break;
    }

    if (!data) return null;

    // Check if data has TTL
    try {
      const parsed = JSON.parse(data);
      if (parsed.expires && Date.now() > parsed.expires) {
        // Expired, remove and return null
        this.removeItem(key, options);
        return null;
      }
      // Return the actual value if TTL structure exists
      if (parsed.value) {
        return JSON.parse(parsed.value);
      }
    } catch {
      // Not a TTL structure, continue
    }

    // Return parsed value
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }

  /**
   * Remove item from storage
   */
  removeItem(key: string, options: SecureStorageOptions): void {
    const { sensitivity } = options;

    switch (sensitivity) {
      case SensitivityLevel.PUBLIC:
      case SensitivityLevel.PRIVATE:
      case SensitivityLevel.SENSITIVE:
        localStorage.removeItem(key);
        break;

      case SensitivityLevel.SESSION:
        sessionStorage.removeItem(key);
        break;
    }
  }

  /**
   * Clear all storage of a specific sensitivity level
   */
  clearBySensitivity(sensitivity: SensitivityLevel): void {
    switch (sensitivity) {
      case SensitivityLevel.SESSION:
        sessionStorage.clear();
        break;

      case SensitivityLevel.PUBLIC:
      case SensitivityLevel.PRIVATE:
      case SensitivityLevel.SENSITIVE:
        // Can't clear selectively, would need to track keys
        logger.warn('[SecureStorage] Use clearAll() to clear localStorage');
        break;
    }
  }

  /**
   * Clear all storage except whitelisted keys
   */
  clearAll(whitelist: string[] = []): void {
    // Clear sessionStorage completely
    sessionStorage.clear();

    // Clear localStorage except whitelist
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !whitelist.includes(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

export const secureStorage = new SecureStorage();

// ============================================================================
// CONVENIENCE HELPERS
// ============================================================================

/**
 * Store public data (theme, language)
 */
export function setPublic(key: string, value: any): void {
  secureStorage.setItem(key, value, { sensitivity: SensitivityLevel.PUBLIC });
}

export function getPublic(key: string): any {
  return secureStorage.getItem(key, { sensitivity: SensitivityLevel.PUBLIC });
}

/**
 * Store session data (cleared on tab close)
 */
export function setSession(key: string, value: any, ttl?: number): void {
  secureStorage.setItem(key, value, { sensitivity: SensitivityLevel.SESSION, ttl });
}

export function getSession(key: string): any {
  return secureStorage.getItem(key, { sensitivity: SensitivityLevel.SESSION });
}

/**
 * Store private data (encrypted in localStorage)
 */
export function setPrivate(key: string, value: any): void {
  secureStorage.setItem(key, value, { sensitivity: SensitivityLevel.PRIVATE });
}

export function getPrivate(key: string): any {
  return secureStorage.getItem(key, { sensitivity: SensitivityLevel.PRIVATE });
}

/**
 * Store sensitive data (encrypted with high security)
 */
export function setSensitive(key: string, value: any): void {
  secureStorage.setItem(key, value, { sensitivity: SensitivityLevel.SENSITIVE });
}

export function getSensitive(key: string): any {
  return secureStorage.getItem(key, { sensitivity: SensitivityLevel.SENSITIVE });
}

/**
 * Clear all storage on logout (preserve user preferences)
 */
export function clearOnLogout(): void {
  const whitelist = [
    STORAGE_KEYS.THEME,
    STORAGE_KEYS.LANGUAGE,
    STORAGE_KEYS.COOKIE_CONSENT,
    // Note: We intentionally DO NOT preserve '__storage_key'
    // A new encryption key will be generated on next login
    // This ensures old encrypted data cannot be accessed after logout
  ];

  secureStorage.clearAll(whitelist);

  // Also clear the old sessionStorage-based key if it exists (migration)
  sessionStorage.removeItem('__session_key');

  logger.debug('[SecureStorage] Cleared all sensitive data on logout');
}
